<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CampaignStatusBadge from './CampaignStatusBadge.vue';
import CampaignDeliveryBar from './CampaignDeliveryBar.vue';

const props = defineProps({
  campaigns: { type: Array, required: true },
});

const emit = defineEmits(['select', 'delete']);

const PER_PAGE = 25;

const { t } = useI18n();
const currentPage = ref(1);

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const channelIcon = channelType => {
  if (!channelType) return '';
  if (channelType.includes('Whatsapp')) return 'whatsapp';
  if (channelType.includes('Sms') || channelType.includes('Twilio'))
    return 'sms';
  if (channelType.includes('Web')) return 'web';
  return '';
};

const audienceLabel = campaign => {
  if (campaign.audience_type === 'spreadsheet') return 'Planilha';
  if (campaign.audience_type === 'slash_command') return 'ERP';
  return 'Labels';
};

const audienceCount = campaign => {
  if (campaign.send_results?.total) return campaign.send_results.total;
  if (campaign.audience_contacts?.length)
    return campaign.audience_contacts.length;
  if (campaign.audience?.length) return campaign.audience.length;
  return '—';
};

const sortedCampaigns = computed(() => {
  return [...props.campaigns].sort((a, b) => b.id - a.id);
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(sortedCampaigns.value.length / PER_PAGE))
);
const paginatedCampaigns = computed(() => {
  const start = (currentPage.value - 1) * PER_PAGE;
  return sortedCampaigns.value.slice(start, start + PER_PAGE);
});
const showingFrom = computed(() =>
  sortedCampaigns.value.length ? (currentPage.value - 1) * PER_PAGE + 1 : 0
);
const showingTo = computed(() =>
  Math.min(currentPage.value * PER_PAGE, sortedCampaigns.value.length)
);

// Reset page when campaigns list changes (filters/tabs)
watch(
  () => props.campaigns.length,
  () => {
    currentPage.value = 1;
  }
);

const goToPage = page => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-weak text-n-slate-9 text-left">
          <th class="py-3 px-3 font-medium text-xs uppercase tracking-wider">
            {{ t('CAMPAIGN.DASHBOARD.TABLE.CAMPAIGN') }}
          </th>
          <th class="py-3 px-3 font-medium text-xs uppercase tracking-wider">
            {{ t('CAMPAIGN.DASHBOARD.TABLE.STATUS') }}
          </th>
          <th
            class="py-3 px-3 font-medium text-xs uppercase tracking-wider min-w-[160px]"
          >
            {{ t('CAMPAIGN.DASHBOARD.TABLE.DELIVERY') }}
          </th>
          <th class="py-3 px-3 font-medium text-xs uppercase tracking-wider">
            {{ t('CAMPAIGN.DASHBOARD.TABLE.AUDIENCE') }}
          </th>
          <th class="py-3 px-3 font-medium text-xs uppercase tracking-wider">
            {{ t('CAMPAIGN.DASHBOARD.TABLE.TEAM') }}
          </th>
          <th class="py-3 px-3 font-medium text-xs uppercase tracking-wider">
            {{ t('CAMPAIGN.DASHBOARD.TABLE.DATE') }}
          </th>
          <th
            class="py-3 px-3 font-medium text-xs uppercase tracking-wider w-20"
          >
            {{ t('CAMPAIGN.DASHBOARD.TABLE.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="campaign in paginatedCampaigns"
          :key="campaign.id"
          class="border-b border-n-weak hover:bg-n-solid-2 transition-colors cursor-pointer group"
          @click="emit('select', campaign)"
        >
          <!-- Campaign name + inbox -->
          <td class="py-3 px-3">
            <div class="font-medium text-n-slate-12 truncate max-w-[240px]">
              {{ campaign.title }}
            </div>
            <div
              v-if="campaign.inbox"
              class="flex items-center gap-1 mt-0.5 text-xs text-n-slate-9"
            >
              <!-- GK-HUB: inline SVG icons instead of letter placeholders -->
              <svg
                v-if="channelIcon(campaign.inbox.channel_type) === 'whatsapp'"
                class="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
                />
              </svg>
              <svg
                v-else-if="channelIcon(campaign.inbox.channel_type) === 'sms'"
                class="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <svg
                v-else
                class="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                />
              </svg>
              <!-- GK-HUB: end inline SVG icons -->
              {{ campaign.inbox.name }}
            </div>
          </td>

          <!-- Status badge -->
          <td class="py-3 px-3">
            <CampaignStatusBadge
              :status="campaign.computed_status || 'active'"
            />
          </td>

          <!-- Delivery bar -->
          <td class="py-3 px-3">
            <CampaignDeliveryBar
              v-if="campaign.campaign_type === 'one_off'"
              :send-results="campaign.send_results"
              :status="campaign.computed_status"
            />
            <span v-else class="text-xs text-n-slate-9">
              {{ campaign.enabled ? 'Ativo' : 'Inativo' }}
            </span>
          </td>

          <!-- Audience -->
          <td class="py-3 px-3">
            <div class="flex items-center gap-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-solid-3 text-n-slate-10 uppercase"
              >
                {{ audienceLabel(campaign) }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ audienceCount(campaign) }}
              </span>
            </div>
          </td>

          <!-- Team -->
          <td class="py-3 px-3">
            <span
              v-if="campaign.team"
              class="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-n-blue-3 text-n-blue-11 border border-n-blue-6"
            >
              {{ campaign.team.name }}
            </span>
            <span v-else class="text-xs text-n-slate-8">—</span>
          </td>

          <!-- Date -->
          <td class="py-3 px-3 text-xs text-n-slate-10 whitespace-nowrap">
            {{ formatDate(campaign.scheduled_at || campaign.created_at) }}
          </td>

          <!-- Actions -->
          <td class="py-3 px-3">
            <div
              class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <button
                class="p-1.5 rounded hover:bg-n-solid-3 text-n-slate-9 hover:text-n-slate-12 transition-colors"
                :title="t('CAMPAIGN.DASHBOARD.ACTIONS.VIEW')"
                @click.stop="emit('select', campaign)"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
              <button
                class="p-1.5 rounded hover:bg-n-ruby-3 text-n-slate-9 hover:text-n-ruby-11 transition-colors"
                :title="t('CAMPAIGN.DASHBOARD.ACTIONS.DELETE')"
                @click.stop="emit('delete', campaign)"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div
      v-if="sortedCampaigns.length > PER_PAGE"
      class="flex items-center justify-between px-4 py-3 border-t border-n-weak"
    >
      <span class="text-[12px] text-n-slate-9">
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        Exibindo {{ showingFrom }} a {{ showingTo }} de
        {{ sortedCampaigns.length }}
        <!-- eslint-enable vue/no-bare-strings-in-template -->
      </span>
      <div class="flex items-center gap-1">
        <button
          class="px-2.5 py-1 text-[12px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-solid-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >
          <!-- eslint-disable vue/no-bare-strings-in-template -->Anterior<!-- eslint-enable vue/no-bare-strings-in-template -->
        </button>
        <span class="text-[12px] text-n-slate-9 px-2 tabular-nums">
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          {{ currentPage }} / {{ totalPages }}
          <!-- eslint-enable vue/no-bare-strings-in-template -->
        </span>
        <button
          class="px-2.5 py-1 text-[12px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-solid-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >
          <!-- eslint-disable vue/no-bare-strings-in-template -->Próximo<!-- eslint-enable vue/no-bare-strings-in-template -->
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!campaigns.length"
      class="flex flex-col items-center justify-center py-16 text-n-slate-9"
    >
      <svg
        class="w-12 h-12 mb-3 text-n-slate-7"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <p class="text-sm">{{ t('CAMPAIGN.DASHBOARD.EMPTY') }}</p>
    </div>
  </div>
</template>
